import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { MapData, RegionCode } from "../../types";
import {
  getPublicMaps,
  likeMap,
  unlikeMap,
  isMapLikedByUser,
  getUserMaps,
} from "../../services/socialService";
import { getVisibleRegions } from "../../services/regionService";
import { Region } from "../../types/regions";
import { useAuth } from "../../contexts/AuthContext";
import { Heart, Calendar, Plus, Search, Grid, List, Globe } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const GalleryContainer = styled.div`
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-color);
  font-size: 2rem;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const MapsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2rem;
  }
`;

const MapCard = styled.div`
  background: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-color);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px var(--shadow-color);
  }

  @media (max-width: 639px) {
    &:hover {
      transform: none;
    }
  }
`;

// Approval status badge
const ApprovalBadge = styled.div<{
  $status: "public" | "pending" | "rejected" | "private" | "approved";
}>`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  background: ${(props) => {
    switch (props.$status) {
      case "public":
        return "#28a745";
      case "pending":
        return "#ffc107";
      case "rejected":
        return "#dc3545";
      case "approved":
        return "#17a2b8";
      case "private":
        return "#6c757d";
      default:
        return "#6c757d";
    }
  }};
  z-index: 10;
`;

const MapThumbnail = styled.div<{ $imageUrl?: string }>`
  width: 100%;
  height: 160px;
  background-image: ${(props) =>
    props.$imageUrl
      ? `url(${props.$imageUrl})`
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"};
  background-size: cover;
  background-position: center;
  border-radius: 8px 8px 0 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  font-weight: 600;

  @media (min-width: 768px) {
    height: 200px;
    font-size: 1.2rem;
  }
`;

const MapInfo = styled.div`
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const MapTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
  font-size: 1.1rem;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 1.3rem;
  }
`;

const MapDescription = styled.p`
  color: var(--text-secondary-color);
  margin: 0 0 1rem 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 0.9rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const MapMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  color: var(--text-secondary-color);

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const UserAvatar = styled.div<{ avatar?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => (props.avatar ? `url(${props.avatar})` : "#007bff")};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Username = styled.span`
  color: var(--text-color);
  font-weight: 500;
`;

const SocialActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const ActionButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${(props) =>
    props.$active ? "var(--error-color)" : "var(--text-secondary-color)"};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 0.9rem;

  &:hover {
    background: ${(props) => (props.$active ? "#fdf2f2" : "#f8f9fa")};
    color: ${(props) => (props.$active ? "#c0392b" : "#333")};
  }
`;

const SkeletonCard = styled.div`
  background: var(--skeleton-bg);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-color);
  overflow: hidden;
  height: 300px;
  position: relative;
  background: linear-gradient(
    90deg,
    var(--skeleton-bg) 25%,
    var(--skeleton-shimmer) 50%,
    var(--skeleton-bg) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 2s infinite;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2rem;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--error-color);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary-color);
`;

const EmptyStateTitle = styled.h3`
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CreateMapCard = styled.div`
  background: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-color);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  border: 2px dashed var(--border-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 250px;

  @media (min-width: 768px) {
    min-height: 300px;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px var(--shadow-color);
    border-color: var(--primary-color);
  }

  @media (max-width: 639px) {
    &:hover {
      transform: none;
    }
  }
`;

const CreateMapContent = styled.div`
  text-align: center;
  padding: 1.5rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const CreateMapIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: white;

  @media (min-width: 768px) {
    width: 64px;
    height: 64px;
  }
`;

const CreateMapTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
  font-size: 1.1rem;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 1.3rem;
  }
`;

const CreateMapDescription = styled.p`
  color: var(--text-secondary-color);
  margin: 0;
  line-height: 1.5;
  font-size: 0.9rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

// Region tabs container
const RegionTabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.5rem;
  background: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--shadow-color);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
    overflow-x: visible;
  }
`;

const RegionTab = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  background: ${(props) => (props.$active ? "var(--primary-color)" : "transparent")};
  color: ${(props) => (props.$active ? "white" : "var(--text-color)")};
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  min-width: max-content;

  &:hover {
    background: ${(props) => (props.$active ? "var(--primary-color)" : "var(--hover-color)")};
  }

  svg {
    opacity: ${(props) => (props.$active ? 1 : 0.7)};
  }
`;

const RegionBadge = styled.span<{ $active: boolean }>`
  background: ${(props) => (props.$active ? "rgba(255, 255, 255, 0.2)" : "var(--badge-bg)")};
  color: ${(props) => (props.$active ? "white" : "var(--text-secondary-color)")};
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

// Controls bar for search, sort, and view toggle
const ControlsBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--surface-color);
  border-radius: 8px;
  box-shadow: 0 2px 8px var(--shadow-color);

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--background-color);
  color: var(--text-color);
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  &::placeholder {
    color: var(--text-secondary-color);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary-color);
  pointer-events: none;
`;

const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SortSelect = styled.select`
  padding: 0.75rem 2rem 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.9rem;
  background: var(--background-color);
  color: var(--text-color);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0.25rem;
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.25rem;
`;

const ViewButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem;
  border: none;
  background: ${(props) => (props.$active ? "var(--primary-color)" : "transparent")};
  color: ${(props) => (props.$active ? "white" : "var(--text-secondary-color)")};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.$active ? "var(--primary-color)" : "var(--surface-color)")};
    color: ${(props) => (props.$active ? "white" : "var(--text-color)")};
  }
`;

// Compact list view
const MapsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const MapListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--surface-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px var(--shadow-color);
    border-color: var(--primary-color);
  }
`;

const ListThumbnail = styled.div<{ $imageUrl?: string }>`
  width: 80px;
  height: 60px;
  min-width: 80px;
  border-radius: 6px;
  background-image: ${(props) =>
    props.$imageUrl
      ? `url(${props.$imageUrl})`
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  position: relative;
`;

const ListContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ListTitle = styled.h3`
  margin: 0;
  color: var(--text-color);
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListDescription = styled.p`
  margin: 0;
  color: var(--text-secondary-color);
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.75rem;
  color: var(--text-secondary-color);
  margin-top: 0.25rem;
`;

const ListBadge = styled.span<{ $status: "public" | "pending" | "rejected" | "private" | "approved" }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${(props) => {
    switch (props.$status) {
      case "public":
        return "#28a745";
      case "pending":
        return "#ffc107";
      case "rejected":
        return "#dc3545";
      case "approved":
        return "#17a2b8";
      case "private":
        return "#6c757d";
      default:
        return "#6c757d";
    }
  }};
  color: white;
`;

const ListActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

interface MapGalleryProps {
  onMapSelect?: (map: MapData) => void;
  maps?: MapData[];
  showCreateMapCard?: boolean;
  hideEmptyState?: boolean;
  showApprovalStatus?: boolean; // New prop for showing approval status
  showUserMapsOnly?: boolean; // New prop for showing only user's own maps
}

type ViewMode = "grid" | "list";
type SortOption = "newest" | "oldest" | "name-asc" | "name-desc" | "likes-desc";

// Region display names for the tabs
const REGION_DISPLAY_NAMES: Record<RegionCode | "all", string> = {
  all: "All Regions",
  iran: "Iran",
  iraq: "Iraq",
  syria: "Syria",
  kurdistan: "Kurdistan",
  baluchistan: "Baluchistan",
  afghanistan: "Afghanistan",
  turkey: "Turkey",
  armenia: "Armenia",
  azerbaijan: "Azerbaijan",
  turkmenistan: "Turkmenistan",
  pakistan: "Pakistan",
  custom: "Custom",
};

const MapGallery: React.FC<MapGalleryProps> = ({
  onMapSelect,
  maps: providedMaps,
  showCreateMapCard = false,
  hideEmptyState = false,
  showApprovalStatus = false,
  showUserMapsOnly = false,
}) => {
  const [maps, setMaps] = useState<MapData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likedMaps, setLikedMaps] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState<SortOption>("newest");
  const [lastSortChange, setLastSortChange] = useState<number>(0);
  const [selectedRegion, setSelectedRegion] = useState<RegionCode | "all">("all");
  const [visibleRegions, setVisibleRegions] = useState<Region[]>([]);
  const { user, canCreateMap } = useAuth();
  const navigate = useNavigate();

  // Fetch visible regions based on user role
  useEffect(() => {
    const fetchVisibleRegions = async () => {
      try {
        const userRole = user?.role || "public";
        const regions = await getVisibleRegions(userRole);
        setVisibleRegions(regions);
      } catch (error) {
        console.error("Error fetching visible regions:", error);
        setVisibleRegions([]);
      }
    };

    fetchVisibleRegions();
  }, [user]);

  // Get visible region codes as a Set for quick lookup
  const visibleRegionCodes = useMemo(() => {
    return new Set(visibleRegions.map((r) => r.code));
  }, [visibleRegions]);

  // Filter maps to only show those from visible regions
  const visibleMaps = useMemo(() => {
    // Admin sees all maps
    if (user?.role === "admin") {
      return maps;
    }

    // Filter maps by visible regions
    return maps.filter((map) => {
      const mapRegion = map.region || "iran"; // Default to iran for legacy maps
      return visibleRegionCodes.has(mapRegion as RegionCode);
    });
  }, [maps, visibleRegionCodes, user]);

  // Check if regions should be visible
  const showRegionTabs = useMemo(() => {
    // Only show tabs if there are visible regions with active status
    if (visibleRegions.length === 0) return false;

    // Get unique regions from visible maps
    const regions = new Set(visibleMaps.map((m) => m.region).filter(Boolean));
    // Show tabs only if there are maps from more than one region
    return regions.size > 1;
  }, [visibleMaps, visibleRegions]);

  // Get available regions with their map counts (only from visible regions)
  const availableRegions = useMemo(() => {
    const regionCounts: Record<string, number> = { all: visibleMaps.length };
    
    visibleMaps.forEach((map) => {
      const region = map.region || "iran"; // Default to iran for legacy maps
      regionCounts[region] = (regionCounts[region] || 0) + 1;
    });

    return Object.entries(regionCounts)
      .filter(([_, count]) => count > 0)
      .map(([region, count]) => ({
        code: region as RegionCode | "all",
        name: REGION_DISPLAY_NAMES[region as RegionCode | "all"] || region,
        count,
      }))
      .sort((a, b) => {
        // "all" always first
        if (a.code === "all") return -1;
        if (b.code === "all") return 1;
        // Then by count descending
        return b.count - a.count;
      });
  }, [visibleMaps]);

  // Debounce search input to prevent excessive filtering operations
  // 300ms delay - reasonable for tests and prevents rapid-fire filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Rate limit sort changes to prevent rapid automated switching
  // Allow max 10 sort changes per second (reasonable for tests, prevents abuse)
  const handleSortChange = (newSort: SortOption) => {
    const now = Date.now();
    const timeSinceLastChange = now - lastSortChange;
    const minInterval = 100; // 100ms = max 10 changes per second

    if (timeSinceLastChange >= minInterval) {
      setSortOption(newSort);
      setLastSortChange(now);
    }
    // Silently ignore rapid changes - won't break tests but prevents abuse
  };

  // Check if user can create maps
  const canCreateNewMap = canCreateMap ? canCreateMap() : false;

  // Simple cache to avoid reloading the same data
  const cacheRef = useRef<{
    maps: MapData[];
    timestamp: number;
    userId: string | null;
  } | null>(null);

  const loadMaps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let mapsToLoad: MapData[] = [];

      if (showUserMapsOnly && user) {
        mapsToLoad = await getUserMaps(user.id);
        setMaps(mapsToLoad);
        // Check which maps are liked by current user (only if user is logged in)
        if (mapsToLoad.length > 0) {
          // Batch check likes for better performance
          const likedMapIds = new Set<string>();

          // Check likes in parallel for better performance
          const likeChecks = mapsToLoad.map((map) =>
            isMapLikedByUser(map.id, user.id).then((isLiked) => ({
              mapId: map.id,
              isLiked,
            }))
          );

          const results = await Promise.all(likeChecks);
          results.forEach(({ mapId, isLiked }) => {
            if (isLiked) {
              likedMapIds.add(mapId);
            }
          });

          setLikedMaps(likedMapIds);
        }
      } else {
        // Load public maps
        mapsToLoad = await getPublicMaps(20);
        setMaps(mapsToLoad);

        // Check which maps are liked by current user (only if user is logged in)
        if (user && mapsToLoad.length > 0) {
          // Batch check likes for better performance
          const likedMapIds = new Set<string>();

          // Check likes in parallel for better performance
          const likeChecks = mapsToLoad.map((map) =>
            isMapLikedByUser(map.id, user.id).then((isLiked) => ({
              mapId: map.id,
              isLiked,
            }))
          );

          const results = await Promise.all(likeChecks);
          results.forEach(({ mapId, isLiked }) => {
            if (isLiked) {
              likedMapIds.add(mapId);
            }
          });

          setLikedMaps(likedMapIds);
        }
      }

      // Update cache
      cacheRef.current = {
        maps: mapsToLoad,
        timestamp: Date.now(),
        userId: user ? user.id : null,
      };
    } catch (err) {
      console.error("Error loading maps:", err);
      setError("Failed to load maps");
    } finally {
      setLoading(false);
    }
  }, [user, showUserMapsOnly]);

  useEffect(() => {
    // If maps are provided, use them directly
    if (providedMaps) {
      setMaps(providedMaps);
      setLoading(false);
      return;
    }

    // Check cache (5 minute cache)
    const now = Date.now();
    const cacheValid =
      cacheRef.current &&
      now - cacheRef.current.timestamp < 5 * 60 * 1000 &&
      cacheRef.current.userId === (user?.id || null);

    if (cacheValid && cacheRef.current) {
      setMaps(cacheRef.current.maps);
      setLoading(false);
      return;
    }

    // Otherwise, load public maps
    loadMaps();
  }, [providedMaps, user?.id, loadMaps, showUserMapsOnly]);

  const handleLike = async (mapId: string) => {
    if (!user) return;

    try {
      const isLiked = likedMaps.has(mapId);

      if (isLiked) {
        await unlikeMap(mapId, user.id);
        setLikedMaps((prev) => {
          const newSet = new Set(prev);
          newSet.delete(mapId);
          return newSet;
        });
        setMaps((prev) =>
          prev.map((map) =>
            map.id === mapId ? { ...map, likesCount: map.likesCount - 1 } : map
          )
        );
      } else {
        await likeMap(mapId, user.id);
        setLikedMaps((prev) => new Set(prev).add(mapId));
        setMaps((prev) =>
          prev.map((map) =>
            map.id === mapId ? { ...map, likesCount: map.likesCount + 1 } : map
          )
        );
      }
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  const handleMapClick = (map: MapData) => {
    if (onMapSelect) {
      onMapSelect(map);
    } else {
      // Navigate to the map page
      navigate(`/map/${map.id}`);
    }
  };

  // Helper function to get approval status
  const getApprovalStatus = (
    map: MapData
  ): "public" | "pending" | "rejected" | "private" | "approved" => {
    if (map.isPublic) return "public";
    if (map.isRejected) return "rejected";
    if (map.approvalRequested) return "pending";
    if (map.isApproved) return "approved";
    return "private";
  };

  // Helper function to get approval status text
  const getApprovalStatusText = (map: MapData): string => {
    if (map.isPublic) return "Public";
    if (map.isRejected) return "Rejected";
    if (map.approvalRequested) return "Pending";
    if (map.isApproved) return "Approved";
    return "Private";
  };

  // Filter and sort maps
  // Using debouncedSearchQuery to prevent excessive filtering on every keystroke
  const filteredAndSortedMaps = useMemo(() => {
    let filtered = [...visibleMaps];

    // Apply region filter
    if (selectedRegion !== "all") {
      filtered = filtered.filter((map) => {
        const mapRegion = map.region || "iran"; // Default to iran for legacy maps
        return mapRegion === selectedRegion;
      });
    }

    // Apply search filter using debounced query
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (map) =>
          map.name.toLowerCase().includes(query) ||
          (map.description && map.description.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortOption) {
        case "newest":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case "oldest":
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "likes-desc":
          return (b.likesCount || 0) - (a.likesCount || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [visibleMaps, debouncedSearchQuery, sortOption, selectedRegion]);

  if (loading) {
    return (
      <GalleryContainer>
        <Title>Explore Maps</Title>
        <SkeletonGrid>
          {[...Array(4)].map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </SkeletonGrid>
      </GalleryContainer>
    );
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  if (maps.length === 0 && !hideEmptyState) {
    return (
      <EmptyState>
        <EmptyStateTitle>No maps found</EmptyStateTitle>
        <p>Be the first to create and share a map!</p>
      </EmptyState>
    );
  }

  // Special case for My Maps page when there are no maps
  if (maps.length === 0 && hideEmptyState) {
    return (
      <GalleryContainer>
        <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
          <h3>No maps yet</h3>
          <p
            style={{
              color: "var(--text-secondary-color)",
              marginBottom: "2rem",
            }}
          >
            {canCreateNewMap
              ? "Create your first map to get started! You can draw states, add information, and share them with the community."
              : "You've reached your map creation limit. Contact support for assistance."}
          </p>
        </div>
        <MapsGrid>
          {showCreateMapCard && canCreateNewMap && (
            <CreateMapCard onClick={() => navigate("/create")}>
              <CreateMapContent>
                <CreateMapIcon>
                  <Plus size={32} />
                </CreateMapIcon>
                <CreateMapTitle>Create Map</CreateMapTitle>
                <CreateMapDescription>
                  Start building your own interactive map
                </CreateMapDescription>
              </CreateMapContent>
            </CreateMapCard>
          )}
        </MapsGrid>
      </GalleryContainer>
    );
  }

  const renderGridView = () => (
    <MapsGrid>
      {filteredAndSortedMaps.map((map) => (
        <MapCard key={map.id} onClick={() => handleMapClick(map)}>
          <MapThumbnail $imageUrl={map.thumbnail}>
            {!map.thumbnail && map.name}
            {showApprovalStatus && (
              <ApprovalBadge $status={getApprovalStatus(map)}>
                {getApprovalStatusText(map)}
              </ApprovalBadge>
            )}
          </MapThumbnail>
          <MapInfo>
            <UserInfo>
              <UserAvatar avatar={map.userAvatar}>
                {!map.userAvatar &&
                  map.userDisplayName.charAt(0).toUpperCase()}
              </UserAvatar>
              <Username>{map.userDisplayName}</Username>
            </UserInfo>

            <MapTitle>{map.name}</MapTitle>
            {map.description && (
              <MapDescription>{map.description}</MapDescription>
            )}

            <MapMeta>
              <MetaItem>
                <Calendar size={16} />
                {formatDistanceToNow(new Date(map.createdAt), {
                  addSuffix: true,
                })}
              </MetaItem>
            </MapMeta>

            <SocialActions onClick={(e) => e.stopPropagation()}>
              <ActionButton
                $active={likedMaps.has(map.id)}
                onClick={() => handleLike(map.id)}
              >
                <Heart
                  size={16}
                  fill={likedMaps.has(map.id) ? "currentColor" : "none"}
                />
                {map.likesCount}
              </ActionButton>
            </SocialActions>
          </MapInfo>
        </MapCard>
      ))}

      {/* Create Map Card */}
      {showCreateMapCard && canCreateNewMap && (
        <CreateMapCard onClick={() => navigate("/create")}>
          <CreateMapContent>
            <CreateMapIcon>
              <Plus size={32} />
            </CreateMapIcon>
            <CreateMapTitle>Create Map</CreateMapTitle>
            <CreateMapDescription>
              Start building your own interactive map
            </CreateMapDescription>
          </CreateMapContent>
        </CreateMapCard>
      )}
    </MapsGrid>
  );

  const renderListView = () => (
    <MapsList>
      {filteredAndSortedMaps.map((map) => (
        <MapListItem key={map.id} onClick={() => handleMapClick(map)}>
          <ListThumbnail $imageUrl={map.thumbnail}>
            {!map.thumbnail && map.name.substring(0, 10)}
            {showApprovalStatus && (
              <ListBadge $status={getApprovalStatus(map)}>
                {getApprovalStatusText(map)}
              </ListBadge>
            )}
          </ListThumbnail>
          <ListContent>
            <ListTitle>{map.name}</ListTitle>
            {map.description && (
              <ListDescription>{map.description}</ListDescription>
            )}
            <ListMeta>
              <MetaItem>
                <Calendar size={12} />
                {formatDistanceToNow(new Date(map.createdAt), {
                  addSuffix: true,
                })}
              </MetaItem>
              {showApprovalStatus && (
                <ListBadge $status={getApprovalStatus(map)}>
                  {getApprovalStatusText(map)}
                </ListBadge>
              )}
            </ListMeta>
          </ListContent>
          <ListActions onClick={(e) => e.stopPropagation()}>
            <ActionButton
              $active={likedMaps.has(map.id)}
              onClick={() => handleLike(map.id)}
            >
              <Heart
                size={14}
                fill={likedMaps.has(map.id) ? "currentColor" : "none"}
              />
              {map.likesCount || 0}
            </ActionButton>
          </ListActions>
        </MapListItem>
      ))}

      {/* Create Map Card in list view */}
      {showCreateMapCard && canCreateNewMap && (
        <MapListItem onClick={() => navigate("/create")}>
          <ListThumbnail>
            <Plus size={24} />
          </ListThumbnail>
          <ListContent>
            <ListTitle>Create New Map</ListTitle>
            <ListDescription>
              Start building your own interactive map
            </ListDescription>
          </ListContent>
        </MapListItem>
      )}
    </MapsList>
  );

  return (
    <GalleryContainer>
      <Title>{showUserMapsOnly ? "My Maps" : "Explore Maps"}</Title>

      {/* Region tabs - show when there are maps from multiple regions */}
      {showRegionTabs && !showUserMapsOnly && (
        <RegionTabsContainer>
          {availableRegions.map((region) => (
            <RegionTab
              key={region.code}
              $active={selectedRegion === region.code}
              onClick={() => setSelectedRegion(region.code)}
            >
              {region.code === "all" && <Globe size={16} />}
              {region.name}
              <RegionBadge $active={selectedRegion === region.code}>
                {region.count}
              </RegionBadge>
            </RegionTab>
          ))}
        </RegionTabsContainer>
      )}
      
      {/* Controls bar - only show for user maps */}
      {showUserMapsOnly && maps.length > 0 && (
        <ControlsBar>
          <SearchContainer>
            <SearchIcon>
              <Search size={18} />
            </SearchIcon>
              <SearchInput
              type="text"
              placeholder="Search maps..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
          </SearchContainer>
          <ControlsRight>
            <SortSelect
              value={sortOption}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSortChange(e.target.value as SortOption)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="likes-desc">Most Liked</option>
            </SortSelect>
            <ViewToggle>
              <ViewButton
                $active={viewMode === "grid"}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <Grid size={18} />
              </ViewButton>
              <ViewButton
                $active={viewMode === "list"}
                onClick={() => setViewMode("list")}
                title="List View"
              >
                <List size={18} />
              </ViewButton>
            </ViewToggle>
          </ControlsRight>
        </ControlsBar>
      )}

      {filteredAndSortedMaps.length === 0 && debouncedSearchQuery ? (
        <EmptyState>
          <EmptyStateTitle>No maps found</EmptyStateTitle>
          <p>Try adjusting your search query</p>
        </EmptyState>
      ) : viewMode === "grid" ? (
        renderGridView()
      ) : (
        renderListView()
      )}
    </GalleryContainer>
  );
};

export default MapGallery;
